import { Badge, Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material'
import Link from 'next/link'
import { useTranslation } from 'react-i18next'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { CopyIdButton } from '@omnea-app/fe-legacy/components/shared/ui/CopyIdButton/CopyIdButton'
import { LoggedInUserAvatar } from '@omnea-app/fe-users-and-groups/avatar/UserAvatar'
import {
  BookOpenOMIcon,
  ChatBubbleOMIcon,
  ChevronDownOMIcon,
  OpenRectArrowOutOMIcon,
  QuestionCircleOMIcon,
  SettingsOMIcon,
  SpaceshipOMIcon,
} from '@omnea-app/icons'
import { useMenu } from '@omnea-app/omui/useMenu'
import { BeamerTrigger } from './BeamerTrigger'
import { logOut } from './logOut'
import { NotificationInbox } from './NotificationInbox'
import { OrganisationIndicator } from './OrganisationIndicator/OrganisationIndicator'
import { useHelpActions } from './useHelpActions'
import { useProductUpdates } from './useProductUpdates'

type UserDetailsView = 'full' | 'compact' | 'minimal'

interface UserDetailsProps {
  view?: UserDetailsView
}

export const UserDetails = ({ view = 'full' }: UserDetailsProps) => {
  const fullView = view === 'full'
  const minimalView = view === 'minimal'

  const userData = useAuthenticatedUser()
  const helpMenu = useMenu()
  const userMenu = useMenu()
  const { t } = useTranslation('common')
  const { showLiveSupport, openKnowledgeBase } = useHelpActions()
  const { isEnabled: isBeamerEnabled, hasUnread: hasBeamerUnread, show: showBeamer } = useProductUpdates()

  if (!userData.isFullyAuthenticated || !userData.user) {
    return null
  }

  const { user } = userData // Reading the `user` property from the `userData` will throw an error if `isFullyAuthenticated` is false

  const showSettingsLink = !user.isSupplier
  const fullName = `${user.firstName} ${user.lastName}`

  return (
    <Stack spacing={4} direction="row" alignItems="center">
      {!minimalView && (
        <>
          <CopyIdButton id={userData.org.organisationId} entityType="organisation" />
          <NotificationInbox />
        </>
      )}
      {isBeamerEnabled && (
        <Tooltip title={t('header.product-updates-tooltip')}>
          <IconButton
            sx={{
              '&.MuiButtonBase-root:hover': {
                bgcolor: 'transparent',
              },
            }}
            onClick={showBeamer}
          >
            <BeamerTrigger />
            <Badge
              variant="dot"
              invisible={!hasBeamerUnread}
              sx={{
                '& .MuiBadge-dot': {
                  bgcolor: 'bg.success.default',
                  top: 4,
                  right: 4,
                  outline: '2px solid',
                  outlineColor: 'border.white',
                },
              }}
            >
              <SpaceshipOMIcon />
            </Badge>
          </IconButton>
        </Tooltip>
      )}
      <IconButton
        sx={{
          gap: fullView ? 1 : 0,
          '&.MuiButtonBase-root:hover': {
            bgcolor: 'transparent',
          },
        }}
        onClick={helpMenu.open}
      >
        <QuestionCircleOMIcon />
        {fullView && <Typography variant="body-regular">{t('header.help')}</Typography>}
      </IconButton>
      <Menu
        open={helpMenu.isOpen}
        anchorEl={helpMenu.anchorEl}
        onClose={helpMenu.close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            helpMenu.close()
            showLiveSupport()
          }}
        >
          <ListItemIcon>
            <ChatBubbleOMIcon />
          </ListItemIcon>
          <ListItemText>{t('header.live-support')}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            helpMenu.close()
            openKnowledgeBase()
          }}
        >
          <ListItemIcon>
            <BookOpenOMIcon />
          </ListItemIcon>
          <ListItemText>{t('header.knowledge-base-link')}</ListItemText>
        </MenuItem>
      </Menu>
      <Stack
        direction="row"
        alignItems="center"
        alignSelf="stretch"
        sx={{
          cursor: 'pointer',
          minWidth: view === 'compact' ? 40 : 'auto',
          color: 'fg.subtle',
        }}
        onClick={userMenu.open}
      >
        <Tooltip title={user.email}>
          {/* Wrapped in div so the tooltip can pass refs */}
          <div>
            <LoggedInUserAvatar user={user} />
          </div>
        </Tooltip>
        {fullView && (
          <Typography variant="body-regular" color="text.muted" noWrap title={fullName} sx={{ ml: 2, maxWidth: 200 }}>
            {fullName}
          </Typography>
        )}
        {fullView && (
          <Box component="span" sx={{ display: 'flex', ml: 1 }}>
            <ChevronDownOMIcon size="14px" data-testid="user-menu" />
          </Box>
        )}
      </Stack>
      <Menu
        open={userMenu.isOpen}
        anchorEl={userMenu.anchorEl}
        onClose={userMenu.close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 200, mt: 1 } } }}
      >
        {showSettingsLink && (
          <MenuItem component={Link} href="/settings/notifications" onClick={userMenu.close}>
            <ListItemIcon>
              <SettingsOMIcon />
            </ListItemIcon>
            <ListItemText>{t('header.settings')}</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            userMenu.close()
            logOut()
          }}
        >
          <ListItemIcon>
            <OpenRectArrowOutOMIcon />
          </ListItemIcon>
          <ListItemText>{t('header.logout')}</ListItemText>
        </MenuItem>
      </Menu>
      {!minimalView && <OrganisationIndicator />}
    </Stack>
  )
}
